import { getDatabase, ref, get, set } from 'firebase/database';

/**
 * Username System für Space-Management
 * Ermöglicht User-Spaces wie: bonusclub.link/username/
 */

/**
 * Prüft ob Spacename verfügbar ist
 */
export const checkSpacenameAvailability = async (spacename) => {
  try {
    const db = getDatabase();
    const spacenameRef = ref(db, `/spacenames/${spacename.toLowerCase()}`);
    const snapshot = await get(spacenameRef);
    
    return !snapshot.exists(); // true = verfügbar, false = belegt
  } catch (error) {
    console.error('Error checking spacename availability:', error);
    return false;
  }
};

/**
 * @deprecated Use checkSpacenameAvailability instead
 */
export const checkUsernameAvailability = checkSpacenameAvailability;

/**
 * Erstellt Username-Mapping
 */
export const createUsername = async (username, userId, displayName = '') => {
  try {
    const db = getDatabase();
    const normalizedUsername = username.toLowerCase();
    
    // Prüfe Verfügbarkeit
    const isAvailable = await checkUsernameAvailability(normalizedUsername);
    if (!isAvailable) {
      throw new Error('Username already taken');
    }
    
    // Erstelle Username-Mapping
    const usernameData = {
      userId,
      displayName,
      createdAt: new Date().toISOString(),
      pageCount: 0
    };
    
    const usernameRef = ref(db, `/usernames/${normalizedUsername}`);
    await set(usernameRef, usernameData);
    
    // Note: No need to store username in user profile anymore
    // Spacenames are managed separately in /spacenames/
    
    return {
      username: normalizedUsername,
      ...usernameData
    };
    
  } catch (error) {
    console.error('Error creating username:', error);
    throw error;
  }
};

/**
 * Lädt Username für User
 */
export const getUserUsername = async (userId) => {
  try {
    // Note: Username no longer stored in /Users/ - using /spacenames/ instead
    // This function is deprecated - use getSpaceBySpacename() instead
    console.warn('getUserUsername is deprecated - use getUserSpaces() from spaceHelpers.js');
    return null;
  } catch (error) {
    console.error('Error loading user username:', error);
    return null;
  }
};

/**
 * Lädt Space-Info anhand des Spacenames
 */
export const getSpaceBySpacename = async (spacename) => {
  try {
    const db = getDatabase();
    const spacenameRef = ref(db, `/spacenames/${spacename.toLowerCase()}`);
    const snapshot = await get(spacenameRef);
    
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error loading space by spacename:', error);
    return null;
  }
};

/**
 * @deprecated Use getSpaceBySpacename instead
 */
export const getUserByUsername = getSpaceBySpacename;

/**
 * Validiert Username-Format
 */
export const validateUsername = (username) => {
  const normalizedUsername = username.toLowerCase().trim();
  
  // Length check
  if (normalizedUsername.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }
  
  if (normalizedUsername.length > 20) {
    return { valid: false, error: 'Username must be less than 20 characters' };
  }
  
  // Format check (nur lowercase, zahlen, bindestriche)
  const usernameRegex = /^[a-z0-9-]+$/;
  if (!usernameRegex.test(normalizedUsername)) {
    return { valid: false, error: 'Username can only contain lowercase letters, numbers, and hyphens' };
  }
  
  // Kann nicht mit Bindestrich beginnen/enden
  if (normalizedUsername.startsWith('-') || normalizedUsername.endsWith('-')) {
    return { valid: false, error: 'Username cannot start or end with a hyphen' };
  }
  
  // Reservierte Usernames
  const reservedUsernames = [
    'admin', 'api', 'www', 'mail', 'ftp', 'localhost', 'root', 'support',
    'help', 'info', 'contact', 'about', 'blog', 'news', 'shop', 'store',
    'app', 'mobile', 'web', 'site', 'page', 'home', 'index', 'main',
    'bonusclub', 'firebase', 'google', 'test', 'demo', 'example'
  ];
  
  if (reservedUsernames.includes(normalizedUsername)) {
    return { valid: false, error: 'Username is reserved' };
  }
  
  return { valid: true, username: normalizedUsername };
};

/**
 * Generiert Username-Vorschläge basierend auf Name/Email
 */
export const generateUsernameOptions = (name = '', email = '') => {
  const suggestions = [];
  
  // Aus Name generieren
  if (name) {
    const cleanName = name.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 15);
    
    if (cleanName.length >= 3) {
      suggestions.push(cleanName);
      suggestions.push(`${cleanName}123`);
      suggestions.push(`${cleanName}2024`);
    }
  }
  
  // Aus Email generieren
  if (email) {
    const emailPart = email.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 15);
    
    if (emailPart.length >= 3 && !suggestions.includes(emailPart)) {
      suggestions.push(emailPart);
      suggestions.push(`${emailPart}site`);
    }
  }
  
  // Default-Optionen
  if (suggestions.length === 0) {
    const randomNum = Math.floor(Math.random() * 9999);
    suggestions.push(`user${randomNum}`);
    suggestions.push(`site${randomNum}`);
    suggestions.push(`page${randomNum}`);
  }
  
  return suggestions.slice(0, 5); // Max 5 Vorschläge
};

/**
 * Lädt alle Pages für einen Username
 */
export const getUserPages = async (username) => {
  try {
    const userInfo = await getUserByUsername(username);
    if (!userInfo) {
      return [];
    }
    
    const db = getDatabase();
    const pagesRef = ref(db, `/pages/${userInfo.userId}`);
    const snapshot = await get(pagesRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const pagesData = snapshot.val();
    return Object.keys(pagesData).map(pageName => ({
      pageName,
      slug: `/${pageName}`,
      ...pagesData[pageName]
    }));
    
  } catch (error) {
    console.error('Error loading user pages:', error);
    return [];
  }
};
